import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import RichTextEditor from "@/components/RichTextEditor";
import { X, Plus, Sparkles, Wand2, ArrowRight, ArrowLeft, CheckSquare, Square, TrendingDown, AlertTriangle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import {
    DndContext, DragOverlay, PointerSensor, useSensor, useSensors, useDraggable, useDroppable,
    closestCenter,
} from "@dnd-kit/core";

function TagInput({ value, onChange, placeholder, testId }) {
    const [v, setV] = useState("");
    const add = (s) => {
        const x = s.trim();
        if (!x) return;
        if (!value.includes(x)) onChange([...value, x]);
        setV("");
    };
    return (
        <div>
            <div className="flex gap-2">
                <Input
                    value={v} onChange={(e) => setV(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === ",") { e.preventDefault(); add(v); } }}
                    placeholder={placeholder}
                    className="rounded-none h-10"
                    data-testid={`${testId}-input`}
                />
                <Button type="button" onClick={() => add(v)} variant="outline" className="rounded-none h-10" data-testid={`${testId}-add`}>
                    <Plus className="w-4 h-4" />
                </Button>
            </div>
            {value.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-3">
                    {value.map((t) => (
                        <span key={t} className="inline-flex items-center gap-1.5 px-2.5 py-1 border border-border bg-white text-xs font-medium">
                            {t}
                            <button type="button" onClick={() => onChange(value.filter((x) => x !== t))} className="text-muted-foreground hover:text-destructive">
                                <X className="w-3 h-3" />
                            </button>
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
}

// ============================================================
// Draggable skill chip + droppable column for Required ↔ Nice-to-have
// ============================================================
function SkillChip({ skill, bucket, onRemove, onMove }) {
    const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
        id: `${bucket}::${skill}`,
        data: { skill, bucket },
    });
    const cls = bucket === "required"
        ? "border-ikb/40 bg-blue-50 text-ikb"
        : "border-emerald-200 bg-emerald-50/60 text-emerald-800";
    return (
        <span
            ref={setNodeRef}
            {...listeners}
            {...attributes}
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 border text-xs font-medium select-none cursor-grab active:cursor-grabbing ${cls} ${isDragging ? "opacity-40" : ""}`}
            data-testid={`skill-chip-${bucket}-${skill}`}
        >
            <span>{skill}</span>
            <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onMove(skill, bucket); }}
                title={bucket === "required" ? "Move to Nice-to-have" : "Move to Required"}
                className="opacity-60 hover:opacity-100"
            >
                {bucket === "required" ? <ArrowRight className="w-3 h-3" /> : <ArrowLeft className="w-3 h-3" />}
            </button>
            <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onRemove(skill, bucket); }}
                className="text-muted-foreground hover:text-destructive"
                title="Remove"
            >
                <X className="w-3 h-3" />
            </button>
        </span>
    );
}

function SkillBucket({ bucket, title, sub, color, skills, onAdd, onRemove, onMove }) {
    const { setNodeRef, isOver } = useDroppable({ id: bucket });
    const [v, setV] = useState("");
    const add = () => {
        const x = v.trim();
        if (!x) return;
        if (!skills.includes(x)) onAdd(x, bucket);
        setV("");
    };
    return (
        <div
            ref={setNodeRef}
            className={`border-2 p-4 transition-colors ${isOver ? "border-ikb bg-blue-50/50" : `border-dashed ${color}`}`}
            data-testid={`skill-bucket-${bucket}`}
        >
            <div className="flex items-end justify-between mb-3 gap-2">
                <div>
                    <h4 className="font-display font-bold text-sm uppercase tracking-widest">{title}</h4>
                    <p className="text-[11px] font-mono text-foreground/70 mt-0.5">{sub}</p>
                </div>
                <span className="text-[10px] font-mono uppercase tracking-widest border border-border bg-white px-2 py-0.5">{skills.length}</span>
            </div>
            <div className="flex flex-wrap gap-2 min-h-[40px]">
                {skills.length === 0 ? (
                    <span className="text-xs text-foreground/50 italic">Drag chips here or add below</span>
                ) : skills.map((s) => (
                    <SkillChip key={s} skill={s} bucket={bucket} onRemove={onRemove} onMove={onMove} />
                ))}
            </div>
            <div className="flex gap-2 mt-3">
                <Input
                    value={v}
                    onChange={(e) => setV(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === ",") { e.preventDefault(); add(); } }}
                    placeholder={`Add a ${bucket === "required" ? "must-have" : "nice-to-have"}…`}
                    className="rounded-none h-9 text-sm"
                    data-testid={`skill-input-${bucket}`}
                />
                <Button type="button" onClick={add} variant="outline" className="rounded-none h-9" data-testid={`skill-add-${bucket}`}>
                    <Plus className="w-4 h-4" />
                </Button>
            </div>
        </div>
    );
}

export default function JobForm({ initial, mode, onSubmit, busy, onCancel }) {
    const [templates, setTemplates] = useState([]);
    const [recruiters, setRecruiters] = useState([]);
    const [industries, setIndustries] = useState([]);
    const [clients, setClients] = useState([]);
    const [similar, setSimilar] = useState([]);
    const [aiBusy, setAiBusy] = useState({ generate: false, extract: false });
    // ---------- Seed matching (state declared up-front, effect defined below `form`) ----------
    const [seedMatches, setSeedMatches] = useState([]);
    const [seedPickedIds, setSeedPickedIds] = useState(new Set());
    const [activeDrag, setActiveDrag] = useState(null);
    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

    const [form, setForm] = useState({
        title: "", description: "",
        location: "", employment_type: "Full-time",
        experience_min: 0, experience_max: 5,
        seniority: "Mid",
        department: "",
        work_mode: "On-site",
        urgency: "Flexible",
        industry: "", sub_industry: "",
        required_skills: [], nice_to_have_skills: [], must_haves: [], red_flags: [],
        status: mode === "edit" ? "active" : "draft",
        priority: "normal",
        hire_start_date: "",
        due_date: "",
        assigned_recruiter_ids: [],
        salary_min: "",
        salary_max: "",
        salary_currency: "INR",
        salary_period: "annual",
        ...(initial || {}),
    });

    useEffect(() => {
        if (initial) {
            // Normalize legacy monthly-period salary values to annual so the
            // form can drop the period selector and always show both bands.
            const next = { ...initial };
            if ((initial.salary_period === "monthly") && (initial.salary_min || initial.salary_max)) {
                if (initial.salary_min) next.salary_min = Number(initial.salary_min) * 12;
                if (initial.salary_max) next.salary_max = Number(initial.salary_max) * 12;
            }
            next.salary_period = "annual";
            setForm((f) => ({ ...f, ...next }));
        }
    }, [initial]);

    // ---------- Seed matching: fetch on title change ----------
    useEffect(() => {
        const t = (form.title || "").trim();
        if (!t || t.length < 3) { setSeedMatches([]); return; }
        const timer = setTimeout(async () => {
            try {
                const { data } = await api.get("/seeds/match", { params: { title: t } });
                setSeedMatches(data?.items || []);
                // Auto-select every match by default — user can untick before generating
                setSeedPickedIds(new Set((data?.items || []).map((s) => s.id)));
            } catch { /* silent */ }
        }, 400);
        return () => clearTimeout(timer);
    }, [form.title]);
    const toggleSeed = (id) => {
        setSeedPickedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };
    const insertSeedManually = (s) => {
        // Append seed copy + union skills/must-haves/red_flags into the form right now.
        setForm((f) => ({
            ...f,
            description: f.description
                ? `${f.description}\n\n${s.description || ""}`.trim()
                : (s.description || ""),
            required_skills: dedupeMerge(f.required_skills, s.required_skills || []),
            nice_to_have_skills: dedupeMerge(f.nice_to_have_skills, s.nice_to_have_skills || []),
            must_haves: dedupeMerge(f.must_haves, s.must_haves || []),
            red_flags: dedupeMerge(f.red_flags, s.red_flags || []),
        }));
        toast.success(`Inserted "${s.name}" into the form`);
    };

    useEffect(() => {
        api.get("/meta/red-flag-templates").then((r) => setTemplates(r.data.templates || [])).catch(() => {});
        api.get("/company/recruiters").then((r) => setRecruiters(r.data || [])).catch(() => {});
        api.get("/meta/industries").then((r) => setIndustries(r.data?.industries || [])).catch(() => {});
        api.get("/clients").then((r) => setClients(r.data?.items || [])).catch(() => {});
    }, []);

    // Duplicate detection on title change (debounced)
    useEffect(() => {
        if (mode === "edit" || !form.title?.trim() || form.title.length < 4) {
            setSimilar([]); return;
        }
        const t = setTimeout(async () => {
            try {
                const { data } = await api.post("/jobs/check-similar", { title: form.title });
                setSimilar(data.matches || []);
            } catch { /* ignore */ }
        }, 500);
        return () => clearTimeout(t);
    }, [form.title, mode]);

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
    const toggleRecruiter = (id) => {
        setForm((f) => {
            const has = (f.assigned_recruiter_ids || []).includes(id);
            return {
                ...f,
                assigned_recruiter_ids: has
                    ? (f.assigned_recruiter_ids || []).filter((x) => x !== id)
                    : [...(f.assigned_recruiter_ids || []), id],
            };
        });
    };
    const toggleFlag = (t) => {
        setForm((f) => ({
            ...f,
            red_flags: f.red_flags.includes(t) ? f.red_flags.filter((x) => x !== t) : [...f.red_flags, t],
        }));
    };
    const allChecked = templates.length > 0 && templates.every((t) => form.red_flags.includes(t));
    const toggleAllFlags = () => {
        setForm((f) => {
            const customs = f.red_flags.filter((x) => !templates.includes(x));
            return { ...f, red_flags: allChecked ? customs : [...templates, ...customs] };
        });
    };

    // Skill mover helpers
    const addSkill = (skill, bucket) => {
        setForm((f) => {
            if (bucket === "required") {
                return { ...f, required_skills: [...f.required_skills, skill] };
            }
            return { ...f, nice_to_have_skills: [...f.nice_to_have_skills, skill] };
        });
    };
    const removeSkill = (skill, bucket) => {
        setForm((f) => bucket === "required"
            ? { ...f, required_skills: f.required_skills.filter((s) => s !== skill) }
            : { ...f, nice_to_have_skills: f.nice_to_have_skills.filter((s) => s !== skill) });
    };
    const moveSkill = (skill, fromBucket) => {
        const to = fromBucket === "required" ? "nice" : "required";
        setForm((f) => {
            const req = fromBucket === "required" ? f.required_skills.filter((s) => s !== skill) : f.required_skills;
            const nice = fromBucket === "nice" ? f.nice_to_have_skills.filter((s) => s !== skill) : f.nice_to_have_skills;
            if (to === "required" && !req.includes(skill)) req.push(skill);
            if (to === "nice" && !nice.includes(skill)) nice.push(skill);
            return { ...f, required_skills: req, nice_to_have_skills: nice };
        });
    };

    // Skill-trend lookup — debounced, called whenever the required list changes
    const [skillTrends, setSkillTrends] = useState({});
    useEffect(() => {
        const names = (form.required_skills || []).slice(0, 30);
        if (names.length === 0) { setSkillTrends({}); return; }
        const h = setTimeout(async () => {
            try {
                const q = names.join(",");
                const { data } = await api.get("/skill-center/skills/lookup", { params: { names: q } });
                const map = {};
                for (const it of (data.items || [])) {
                    if (it.trend_status && ["declining", "obsolete"].includes(it.trend_status)) {
                        map[it.query] = it;
                    }
                }
                setSkillTrends(map);
            } catch (err) { /* silent — non-blocking */ }
        }, 450);
        return () => clearTimeout(h);
    }, [form.required_skills]);

    const decliningInRequired = Object.values(skillTrends);
    const swapSkill = (fromName, toName) => {
        if (!toName) return;
        setForm((f) => {
            const req = f.required_skills.map((s) => (s === fromName ? toName : s));
            return { ...f, required_skills: Array.from(new Set(req)) };
        });
        toast.success(`Swapped "${fromName}" → "${toName}"`);
    };
    const removeRequired = (name) => {
        setForm((f) => ({ ...f, required_skills: f.required_skills.filter((s) => s !== name) }));
    };

    // Soft-confirm dialog on save when declining skills remain
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [pendingPayload, setPendingPayload] = useState(null);

    const onDragStart = (e) => setActiveDrag(e.active?.data?.current?.skill || null);
    const onDragEnd = (e) => {
        setActiveDrag(null);
        const over = e.over?.id; // "required" or "nice"
        const data = e.active?.data?.current;
        if (!over || !data) return;
        const fromBucket = data.bucket;
        if (fromBucket === over) return;
        moveSkill(data.skill, fromBucket);
    };

    // ============================================================
    // AI assist handlers
    // ============================================================
    const aiGenerateJD = async () => {
        if (!form.title.trim()) { toast.error("Enter a job title first"); return; }
        setAiBusy((b) => ({ ...b, generate: true }));
        try {
            const { data } = await api.post("/jobs/ai/generate-description", {
                title: form.title, seniority: form.seniority,
                employment_type: form.employment_type,
                experience_min: form.experience_min, experience_max: form.experience_max,
                location: form.location,
                seed_ids: [...seedPickedIds],
                use_matching_seeds: seedPickedIds.size === 0,
            });
            setForm((f) => ({
                ...f,
                description: data.description || f.description,
                required_skills: dedupeMerge(f.required_skills, data.required_skills || []),
                nice_to_have_skills: dedupeMerge(f.nice_to_have_skills, data.nice_to_have_skills || []),
                must_haves: dedupeMerge(f.must_haves, data.must_haves || []),
                red_flags: dedupeMerge(f.red_flags, data.red_flags || []),
            }));
            const seedNames = (data.seeds_used || []).map((s) => s.name);
            const seedSuffix = seedNames.length ? ` · used seeds: ${seedNames.join(", ")}` : "";
            toast.success(`AI drafted JD + ${(data.required_skills || []).length + (data.nice_to_have_skills || []).length} skills${seedSuffix}`);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally { setAiBusy((b) => ({ ...b, generate: false })); }
    };

    const aiExtractSkills = async () => {
        if (!(form.description || "").trim() || form.description.length < 30) {
            toast.error("Add a description first (at least 30 characters)");
            return;
        }
        setAiBusy((b) => ({ ...b, extract: true }));
        try {
            const { data } = await api.post("/jobs/ai/extract-skills", {
                description: form.description, title: form.title,
            });
            setForm((f) => ({
                ...f,
                required_skills: dedupeMerge(f.required_skills, data.required_skills || []),
                nice_to_have_skills: dedupeMerge(f.nice_to_have_skills, data.nice_to_have_skills || []),
            }));
            const total = (data.required_skills || []).length + (data.nice_to_have_skills || []).length;
            toast.success(total ? `Extracted ${total} skill${total === 1 ? "" : "s"} from JD` : "No new skills found");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally { setAiBusy((b) => ({ ...b, extract: false })); }
    };

    const [jdAnalyzing, setJdAnalyzing] = useState(false);
    const [jdReport, setJdReport] = useState(null);
    const analyzeJd = async () => {
        setJdAnalyzing(true);
        try {
            const { data } = await api.post("/jobs/quality/preview", {
                title: form.title,
                description: form.description,
                location: form.location,
                work_mode: form.work_mode,
                employment_type: form.employment_type,
                skills_required: form.required_skills,
                skills_nice_to_have: form.nice_to_have_skills,
                compensation: form.compensation,
                openings: form.openings,
                openings_count: form.openings_count,
                experience_years_min: form.experience_years_min,
                experience_years_max: form.experience_years_max,
            });
            setJdReport(data);
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setJdAnalyzing(false); }
    };

    const submit = (e) => {
        e.preventDefault();
        const payload = { ...form };
        // Always normalize compensation to annual on the wire — the UI shows
        // both Annual and Monthly fields but storage stays single-period.
        payload.salary_period = "annual";
        // Coerce salary fields to numbers (or null)
        for (const k of ["salary_min", "salary_max"]) {
            const v = payload[k];
            if (v === "" || v === null || v === undefined) payload[k] = null;
            else payload[k] = Number(v);
        }
        // Soft confirm if declining skills remain in required
        if (decliningInRequired.length > 0) {
            setPendingPayload(payload);
            setConfirmOpen(true);
            return;
        }
        onSubmit(payload);
    };

    const forceSubmit = () => {
        if (pendingPayload) {
            const p = pendingPayload;
            setPendingPayload(null);
            setConfirmOpen(false);
            onSubmit(p);
        }
    };

    return (
        <form onSubmit={submit} className="space-y-10">
            <section className="border border-border bg-white p-4 sm:p-6 lg:p-8 space-y-6">
                <h2 className="font-display font-bold text-xl">Basics</h2>
                <div className="grid md:grid-cols-2 gap-6">
                    <div className="md:col-span-2">
                        <div className="flex items-end justify-between gap-3 flex-wrap">
                            <Label className="text-xs uppercase tracking-widest font-bold">Job title</Label>
                            <Button
                                type="button"
                                onClick={aiGenerateJD}
                                disabled={aiBusy.generate || !form.title.trim()}
                                variant="outline"
                                className="rounded-none h-8 text-xs border-ikb text-ikb hover:bg-ikb hover:text-white"
                                data-testid="ai-generate-jd-btn"
                                title="Use AI to draft a JD + skills from this title"
                            >
                                <Sparkles className="w-3.5 h-3.5 mr-1.5" />
                                {aiBusy.generate ? "Generating…" : "Generate JD with AI"}
                            </Button>
                        </div>
                        <Input required value={form.title} onChange={(e) => set("title", e.target.value)} className="rounded-none mt-2 h-11" placeholder="Senior Backend Engineer" data-testid="job-title" />
                        {seedMatches.length > 0 && (
                            <div className="mt-3 border border-ikb/30 bg-blue-50/50 p-3 space-y-2" data-testid="seed-match-panel">
                                <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-ikb">
                                    <Sparkles className="w-3.5 h-3.5" /> {seedMatches.length} JD seed{seedMatches.length === 1 ? "" : "s"} match this title
                                </div>
                                <p className="text-[11px] text-foreground/70">Checked seeds will be passed to the AI generator OR you can insert them directly.</p>
                                <ul className="space-y-1.5">
                                    {seedMatches.map((s) => (
                                        <li key={s.id} className="flex items-center justify-between gap-3 flex-wrap" data-testid={`seed-match-${s.id}`}>
                                            <label className="inline-flex items-center gap-2 text-sm cursor-pointer min-w-0">
                                                <input
                                                    type="checkbox"
                                                    checked={seedPickedIds.has(s.id)}
                                                    onChange={() => toggleSeed(s.id)}
                                                    data-testid={`seed-match-check-${s.id}`}
                                                />
                                                <span className="font-medium truncate">{s.name}</span>
                                                <span className="text-[10px] font-mono text-foreground/60">
                                                    {(s.required_skills || []).length}r · {(s.nice_to_have_skills || []).length}n · {(s.must_haves || []).length}m · {(s.red_flags || []).length}rf
                                                </span>
                                            </label>
                                            <button
                                                type="button"
                                                onClick={() => insertSeedManually(s)}
                                                className="text-[10px] font-mono uppercase tracking-widest px-2 py-1 border border-ikb text-ikb hover:bg-ikb hover:text-white"
                                                data-testid={`seed-match-insert-${s.id}`}
                                            >Insert manually</button>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        {similar.length > 0 && (
                            <div className="mt-2 border border-amber-300 bg-amber-50 p-3 text-xs" data-testid="job-similar-warning">
                                <div className="flex items-center gap-1.5 font-mono uppercase tracking-widest text-amber-800 mb-1.5">
                                    <span>⚠</span> {similar.length} similar active opening{similar.length === 1 ? "" : "s"} already exist{similar.length === 1 ? "s" : ""}
                                </div>
                                <ul className="space-y-0.5">
                                    {similar.map((s) => (
                                        <li key={s.id} className="text-foreground/80">
                                            <a href={`/jobs/${s.id}`} target="_blank" rel="noreferrer" className="text-ikb hover:underline">{s.title}</a>
                                            {" · "}<span className="font-mono">{Math.round(s.similarity * 100)}% match</span>
                                            {s.location && <> · {s.location}</>}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                    <div className="md:col-span-2">
                        <div className="flex items-end justify-between gap-3 flex-wrap">
                            <div>
                                <Label className="text-xs uppercase tracking-widest font-bold">Job description (rich text)</Label>
                                {(() => {
                                    const plain = (form.description || "").replace(/<[^>]+>/g, " ").trim();
                                    const words = plain ? plain.split(/\s+/).filter(Boolean).length : 0;
                                    const chars = plain.length;
                                    return (
                                        <div className="text-[10px] font-mono uppercase tracking-widest text-foreground/60 mt-1" data-testid="jd-wordcount">
                                            {words} words · {chars} chars
                                        </div>
                                    );
                                })()}
                            </div>
                            <div className="flex items-center gap-2">
                                <Button
                                    type="button"
                                    onClick={analyzeJd}
                                    disabled={jdAnalyzing || ((form.description || "").replace(/<[^>]+>/g, " ").trim().length < 50)}
                                    variant="outline"
                                    className="rounded-none h-8 text-xs"
                                    data-testid="jd-analyze-btn"
                                    title="Score the JD on completeness, clarity and bias"
                                >
                                    <Sparkles className="w-3.5 h-3.5 mr-1.5" />
                                    {jdAnalyzing ? "Analyzing…" : "Analyze JD"}
                                </Button>
                                <Button
                                    type="button"
                                    onClick={aiExtractSkills}
                                    disabled={aiBusy.extract || !(form.description || "").trim()}
                                    variant="outline"
                                    className="rounded-none h-8 text-xs"
                                    data-testid="ai-extract-skills-btn"
                                    title="Pull required + nice-to-have skills from the description"
                                >
                                    <Wand2 className="w-3.5 h-3.5 mr-1.5" />
                                    {aiBusy.extract ? "Extracting…" : "Auto-fill skills from JD"}
                                </Button>
                            </div>
                        </div>
                        <div className="mt-2">
                            <RichTextEditor
                                value={form.description}
                                onChange={(v) => set("description", v)}
                                placeholder="Write the role overview, responsibilities, what you offer…"
                                minHeight={260}
                                testId="job-description"
                            />
                        </div>
                        {jdReport && (
                            <div className="mt-2 border border-border bg-white" data-testid="jd-report">
                                <div className="px-3 py-2 border-b border-border flex items-center justify-between bg-secondary/40">
                                    <div className="flex items-center gap-2">
                                        <span className="text-[10px] font-mono uppercase tracking-widest text-foreground/60">JD score</span>
                                        <span className={`font-display font-black text-xl ${jdReport.score >= 80 ? "text-emerald-700" : jdReport.score >= 60 ? "text-amber-600" : "text-rose-700"}`}>{jdReport.score}/100</span>
                                        <span className={`text-[10px] font-mono uppercase tracking-widest border px-1.5 py-0.5 ${jdReport.tier === "great" ? "bg-emerald-100 text-emerald-700 border-emerald-300" : jdReport.tier === "good" ? "bg-blue-100 text-ikb border-ikb" : jdReport.tier === "fair" ? "bg-amber-100 text-amber-700 border-amber-300" : "bg-rose-100 text-rose-700 border-rose-300"}`}>{jdReport.label || jdReport.tier}</span>
                                    </div>
                                    <button type="button" onClick={() => setJdReport(null)} className="text-[10px] font-mono uppercase tracking-widest text-foreground/50 hover:text-foreground" data-testid="jd-report-close">Dismiss</button>
                                </div>
                                {(jdReport.issues || []).length === 0 ? (
                                    <div className="p-3 text-xs text-emerald-700">No issues found — your JD is in great shape.</div>
                                ) : (
                                    <ul className="p-3 space-y-1.5 text-xs">
                                        {(jdReport.issues || []).map((it, i) => (
                                            <li key={i} className="flex items-start gap-2" data-testid={`jd-issue-${i}`}>
                                                <span className="text-rose-500 mt-0.5">•</span>
                                                <span>
                                                    {typeof it === "string" ? it : (
                                                        <>
                                                            <span className="font-medium">{it.label || it.reason || "Issue"}</span>
                                                            {it.points ? <span className="text-[10px] font-mono text-foreground/50 ml-2">−{it.points} pts</span> : null}
                                                        </>
                                                    )}
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                                {(jdReport.formatting_issues || []).length > 0 && (
                                    <div className="border-t border-border bg-amber-50/50 p-3" data-testid="jd-formatting-issues">
                                        <div className="text-[10px] font-mono uppercase tracking-widest text-amber-700 mb-1.5">Formatting & presentation</div>
                                        <ul className="space-y-1 text-xs text-foreground/80">
                                            {jdReport.formatting_issues.map((s, i) => (
                                                <li key={i} className="flex items-start gap-2" data-testid={`jd-format-issue-${i}`}>
                                                    <span className="text-amber-600 mt-0.5">•</span>
                                                    <span>{s}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                                {(jdReport.writing_issues || []).length > 0 && (
                                    <div className="border-t border-border bg-rose-50/50 p-3" data-testid="jd-writing-issues">
                                        <div className="text-[10px] font-mono uppercase tracking-widest text-rose-700 mb-1.5">Writing quality</div>
                                        <ul className="space-y-1 text-xs text-foreground/80">
                                            {jdReport.writing_issues.map((s, i) => (
                                                <li key={i} className="flex items-start gap-2" data-testid={`jd-writing-issue-${i}`}>
                                                    <span className="text-rose-600 mt-0.5">•</span>
                                                    <span>{s}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                    <div>
                        <Label className="text-xs uppercase tracking-widest font-bold">Location</Label>
                        <Input value={form.location} onChange={(e) => set("location", e.target.value)} className="rounded-none mt-2 h-11" placeholder="Remote · Berlin · NYC..." data-testid="job-location" />
                    </div>
                    <div>
                        <Label className="text-xs uppercase tracking-widest font-bold">Employment type</Label>
                        <Select value={form.employment_type} onValueChange={(v) => set("employment_type", v)}>
                            <SelectTrigger className="rounded-none mt-2 h-11" data-testid="job-employment-type"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {["Full-time","Part-time","Contract","Internship","Freelance"].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="text-xs uppercase tracking-widest font-bold">Seniority</Label>
                        <Select value={form.seniority} onValueChange={(v) => set("seniority", v)}>
                            <SelectTrigger className="rounded-none mt-2 h-11" data-testid="job-seniority"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {["Intern","Junior","Mid","Senior","Staff","Principal","Director"].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="text-xs uppercase tracking-widest font-bold">Department</Label>
                        <Input
                            value={form.department || ""}
                            onChange={(e) => set("department", e.target.value)}
                            className="rounded-none mt-2 h-11"
                            placeholder="Engineering · Sales · Marketing…"
                            data-testid="job-department"
                            list="job-department-suggestions"
                        />
                        <datalist id="job-department-suggestions">
                            {["Engineering", "Product", "Design", "Sales", "Marketing", "Customer Success",
                              "Operations", "Finance", "People / HR", "Legal", "IT", "Data & Analytics",
                              "Research", "Support"].map((d) => <option key={d} value={d} />)}
                        </datalist>
                    </div>
                    <div>
                        <Label className="text-xs uppercase tracking-widest font-bold">Client</Label>
                        <Select value={form.client_id || "__none__"} onValueChange={(v) => set("client_id", v === "__none__" ? null : v)}>
                            <SelectTrigger className="rounded-none mt-2 h-11" data-testid="job-client"><SelectValue placeholder="Internal (no client)" /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="__none__">Internal — no client</SelectItem>
                                {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="text-xs uppercase tracking-widest font-bold">Work mode</Label>
                        <Select value={form.work_mode || "On-site"} onValueChange={(v) => set("work_mode", v)}>
                            <SelectTrigger className="rounded-none mt-2 h-11" data-testid="job-work-mode"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {["On-site", "Hybrid", "Remote"].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="text-xs uppercase tracking-widest font-bold">Urgency</Label>
                        <Select value={form.urgency || "Flexible"} onValueChange={(v) => set("urgency", v)}>
                            <SelectTrigger className="rounded-none mt-2 h-11" data-testid="job-urgency"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {["Immediate", "15 days", "30 days", "Flexible"].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="text-xs uppercase tracking-widest font-bold">Industry</Label>
                        <Select value={form.industry || ""} onValueChange={(v) => setForm((f) => ({ ...f, industry: v, sub_industry: "" }))}>
                            <SelectTrigger className="rounded-none mt-2 h-11" data-testid="job-industry"><SelectValue placeholder="Select industry…" /></SelectTrigger>
                            <SelectContent>
                                {industries.map((i) => <SelectItem key={i.name} value={i.name}>{i.name}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="text-xs uppercase tracking-widest font-bold">Sub-industry</Label>
                        <Select
                            value={form.sub_industry || ""}
                            onValueChange={(v) => set("sub_industry", v)}
                            disabled={!form.industry}
                        >
                            <SelectTrigger className="rounded-none mt-2 h-11" data-testid="job-sub-industry">
                                <SelectValue placeholder={form.industry ? "Pick a sub-industry…" : "Select industry first"} />
                            </SelectTrigger>
                            <SelectContent>
                                {(industries.find((i) => i.name === form.industry)?.sub || []).map((s) => (
                                    <SelectItem key={s} value={s}>{s}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label className="text-xs uppercase tracking-widest font-bold">Min YoE</Label>
                            <Input type="number" min={0} value={form.experience_min} onChange={(e) => set("experience_min", parseInt(e.target.value || "0"))} className="rounded-none mt-2 h-11" data-testid="job-exp-min" />
                        </div>
                        <div>
                            <Label className="text-xs uppercase tracking-widest font-bold">Max YoE</Label>
                            <Input type="number" min={0} value={form.experience_max} onChange={(e) => set("experience_max", parseInt(e.target.value || "0"))} className="rounded-none mt-2 h-11" data-testid="job-exp-max" />
                        </div>
                    </div>
                    {mode === "edit" && (
                        <div>
                            <Label className="text-xs uppercase tracking-widest font-bold">Status</Label>
                            <Select value={form.status} onValueChange={(v) => set("status", v)}>
                                <SelectTrigger className="rounded-none mt-2 h-11" data-testid="job-status"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {["draft","active","paused","closed"].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                    )}
                </div>
            </section>

            <section className="border border-border bg-white p-4 sm:p-6 lg:p-8 space-y-6">
                <h2 className="font-display font-bold text-xl">Pipeline · timing · ownership</h2>
                <p className="text-sm text-muted-foreground -mt-3">Track urgency, target dates, and which recruiters are working this opening.</p>
                <div className="grid md:grid-cols-2 gap-6">
                    {mode !== "edit" && (
                        <div>
                            <Label className="text-xs uppercase tracking-widest font-bold">Publish status</Label>
                            <Select value={form.status} onValueChange={(v) => set("status", v)}>
                                <SelectTrigger className="rounded-none mt-2 h-11" data-testid="job-status"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {[
                                        ["draft", "Draft — not visible publicly"],
                                        ["active", "Active — live & accepting applications"],
                                        ["paused", "Paused — temporarily not accepting"],
                                        ["closed", "Closed"],
                                    ].map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                    )}
                    <div>
                        <Label className="text-xs uppercase tracking-widest font-bold">Priority</Label>
                        <Select value={form.priority || "normal"} onValueChange={(v) => set("priority", v)}>
                            <SelectTrigger className="rounded-none mt-2 h-11" data-testid="job-priority"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {[
                                    ["low", "Low"],
                                    ["normal", "Normal"],
                                    ["high", "High"],
                                    ["urgent", "Urgent"],
                                ].map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="text-xs uppercase tracking-widest font-bold">Hiring start date</Label>
                        <Input type="date" value={form.hire_start_date || ""} onChange={(e) => set("hire_start_date", e.target.value)} className="rounded-none mt-2 h-11" data-testid="job-hire-start-date" />
                    </div>
                    <div>
                        <Label className="text-xs uppercase tracking-widest font-bold">Due date (target close)</Label>
                        <Input type="date" value={form.due_date || ""} onChange={(e) => set("due_date", e.target.value)} className="rounded-none mt-2 h-11" data-testid="job-due-date" />
                    </div>
                </div>

                {recruiters.length > 0 && (
                    <div>
                        <Label className="text-xs uppercase tracking-widest font-bold mb-2 block">Assigned recruiters</Label>
                        <div className="grid md:grid-cols-2 gap-2">
                            {recruiters.map((r) => {
                                const checked = (form.assigned_recruiter_ids || []).includes(r.id);
                                return (
                                    <label
                                        key={r.id}
                                        className={`flex items-center gap-3 border p-3 cursor-pointer transition-colors ${checked ? "border-ikb bg-blue-50/40" : "border-border bg-white hover:bg-secondary"}`}
                                    >
                                        <Checkbox checked={checked} onCheckedChange={() => toggleRecruiter(r.id)} className="rounded-none" data-testid={`assign-rec-${r.id}`} />
                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm font-medium truncate">{r.name}</div>
                                            <div className="text-xs text-muted-foreground font-mono truncate">{r.email} · {r.role}</div>
                                        </div>
                                    </label>
                                );
                            })}
                        </div>
                    </div>
                )}
            </section>

            <section className="border border-border bg-white p-4 sm:p-6 lg:p-8 space-y-6">
                <div>
                    <h2 className="font-display font-bold text-xl">Compensation band</h2>
                    <p className="text-sm text-muted-foreground mt-1">
                        Used to flag candidates as under / at / over budget in the Kanban, lists and AI analysis.
                        Type either the Annual or Monthly amount — the other auto-fills (Monthly = Annual ÷ 12).
                    </p>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                    <div>
                        <Label className="text-xs uppercase tracking-widest font-bold">Currency</Label>
                        <Select value={form.salary_currency || "INR"} onValueChange={(v) => set("salary_currency", v)}>
                            <SelectTrigger className="rounded-none mt-2 h-11" data-testid="job-salary-currency"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {["INR","USD","EUR","GBP","AED","SGD","AUD","CAD"].map((x) => (
                                    <SelectItem key={x} value={x}>{x}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </div>
                <div className="grid md:grid-cols-2 gap-6">
                    <div className="border border-border p-3 sm:p-4 space-y-3">
                        <div className="text-[10px] font-mono uppercase tracking-widest text-ikb">Annual</div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label className="text-xs uppercase tracking-widest font-bold">Min</Label>
                                <Input
                                    type="number" min={0} step={10000}
                                    value={form.salary_min ?? ""}
                                    onChange={(e) => {
                                        const v = e.target.value;
                                        set("salary_min", v);
                                    }}
                                    placeholder="e.g. 1500000"
                                    className="rounded-none mt-2 h-11"
                                    data-testid="job-salary-min"
                                />
                                <div className="mt-1 text-[10px] font-mono text-muted-foreground" data-testid="job-salary-min-monthly-derived">
                                    {form.salary_min ? `≈ ${Math.round(Number(form.salary_min) / 12).toLocaleString()} / mo` : ""}
                                </div>
                            </div>
                            <div>
                                <Label className="text-xs uppercase tracking-widest font-bold">Max</Label>
                                <Input
                                    type="number" min={0} step={10000}
                                    value={form.salary_max ?? ""}
                                    onChange={(e) => set("salary_max", e.target.value)}
                                    placeholder="e.g. 2500000"
                                    className="rounded-none mt-2 h-11"
                                    data-testid="job-salary-max"
                                />
                                <div className="mt-1 text-[10px] font-mono text-muted-foreground" data-testid="job-salary-max-monthly-derived">
                                    {form.salary_max ? `≈ ${Math.round(Number(form.salary_max) / 12).toLocaleString()} / mo` : ""}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="border border-border p-3 sm:p-4 space-y-3">
                        <div className="text-[10px] font-mono uppercase tracking-widest text-ikb">Monthly</div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label className="text-xs uppercase tracking-widest font-bold">Min</Label>
                                <Input
                                    type="number" min={0} step={1000}
                                    value={form.salary_min ? Math.round(Number(form.salary_min) / 12) : ""}
                                    onChange={(e) => {
                                        const m = e.target.value;
                                        set("salary_min", m === "" ? "" : String(Math.round(Number(m) * 12)));
                                    }}
                                    placeholder="e.g. 125000"
                                    className="rounded-none mt-2 h-11"
                                    data-testid="job-salary-min-monthly"
                                />
                            </div>
                            <div>
                                <Label className="text-xs uppercase tracking-widest font-bold">Max</Label>
                                <Input
                                    type="number" min={0} step={1000}
                                    value={form.salary_max ? Math.round(Number(form.salary_max) / 12) : ""}
                                    onChange={(e) => {
                                        const m = e.target.value;
                                        set("salary_max", m === "" ? "" : String(Math.round(Number(m) * 12)));
                                    }}
                                    placeholder="e.g. 210000"
                                    className="rounded-none mt-2 h-11"
                                    data-testid="job-salary-max-monthly"
                                />
                            </div>
                        </div>
                    </div>
                </div>
                {form.salary_min && form.salary_max && Number(form.salary_min) > Number(form.salary_max) && (
                    <p className="text-xs text-rose-600 font-mono" data-testid="job-salary-warning">
                        Min is greater than max — please review.
                    </p>
                )}
            </section>

            <section className="border border-border bg-white p-4 sm:p-6 lg:p-8 space-y-6">
                <div className="flex items-end justify-between gap-3 flex-wrap">
                    <div>
                        <h2 className="font-display font-bold text-xl">Skills</h2>
                        <p className="text-sm text-muted-foreground mt-1">Drag chips between buckets, or use the arrow on each chip to move it. Auto-fill from JD pulls suggestions straight from your description.</p>
                    </div>
                </div>
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={onDragStart} onDragEnd={onDragEnd}>
                    <div className="grid md:grid-cols-2 gap-4">
                        <SkillBucket
                            bucket="required"
                            title="Must-have skills"
                            sub="Required — used for must-have match scoring"
                            color="border-ikb/30"
                            skills={form.required_skills}
                            onAdd={addSkill} onRemove={removeSkill} onMove={moveSkill}
                        />
                        <SkillBucket
                            bucket="nice"
                            title="Nice-to-have skills"
                            sub="Bonus — used for nice-to-have match scoring"
                            color="border-emerald-300"
                            skills={form.nice_to_have_skills}
                            onAdd={addSkill} onRemove={removeSkill} onMove={moveSkill}
                        />
                    </div>
                    <DragOverlay>
                        {activeDrag ? (
                            <span className="inline-flex items-center px-2.5 py-1 border border-ikb bg-blue-50 text-ikb text-xs font-medium shadow-md">{activeDrag}</span>
                        ) : null}
                    </DragOverlay>
                </DndContext>

                {/* Trend audit: warn on declining/obsolete required skills */}
                {decliningInRequired.length > 0 && (
                    <div className="border border-amber-400 bg-amber-50 p-3" data-testid="jobform-trend-audit">
                        <div className="text-[11px] font-mono uppercase tracking-widest text-amber-800 mb-2 flex items-center gap-1.5">
                            <TrendingDown className="w-3.5 h-3.5" />
                            {decliningInRequired.length} required skill{decliningInRequired.length === 1 ? "" : "s"} flagged as declining or obsolete
                        </div>
                        <div className="space-y-1.5">
                            {decliningInRequired.map((t) => (
                                <div key={t.query} className="flex items-center justify-between gap-2 text-xs bg-white border border-amber-200 px-2 py-1.5" data-testid={`jobform-declining-${t.query.replace(/\s+/g, "-").toLowerCase()}`}>
                                    <div className="min-w-0 flex-1">
                                        <span className="font-medium">{t.query}</span>
                                        <span className={`ml-2 text-[10px] font-mono uppercase tracking-widest px-1 border ${t.trend_status === "obsolete" ? "border-rose-500 text-rose-700 bg-rose-50" : "border-amber-500 text-amber-700 bg-amber-50"}`}>
                                            {t.trend_status}{typeof t.trend_score === "number" ? ` · ${t.trend_score}` : ""}
                                        </span>
                                        {t.trend_reason && <span className="ml-2 text-[10px] text-muted-foreground">{t.trend_reason}</span>}
                                    </div>
                                    <div className="flex items-center gap-1 shrink-0">
                                        {t.trend_replaced_by && (
                                            <button
                                                type="button"
                                                onClick={() => swapSkill(t.query, t.trend_replaced_by)}
                                                className="text-[10px] font-mono uppercase tracking-widest border border-emerald-500 bg-emerald-50 text-emerald-800 px-2 py-0.5 hover:bg-emerald-100"
                                                data-testid={`jobform-swap-${t.query.replace(/\s+/g, "-").toLowerCase()}`}
                                            >
                                                Swap → {t.trend_replaced_by}
                                            </button>
                                        )}
                                        <button
                                            type="button"
                                            onClick={() => removeRequired(t.query)}
                                            className="text-[10px] font-mono uppercase tracking-widest border border-border bg-white px-2 py-0.5 hover:bg-secondary"
                                            data-testid={`jobform-drop-${t.query.replace(/\s+/g, "-").toLowerCase()}`}
                                        >
                                            Remove
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
                <div className="pt-2">
                    <Label className="text-xs uppercase tracking-widest font-bold mb-2 block">Must-have behavioural / experience criteria</Label>
                    <TagInput value={form.must_haves} onChange={(v) => set("must_haves", v)} placeholder="e.g. Led a team of 5+, Built scalable APIs..." testId="musthaves" />
                </div>
            </section>

            <section className="border border-border bg-white p-4 sm:p-6 lg:p-8 space-y-4">
                <div className="flex items-end justify-between gap-3 flex-wrap">
                    <div>
                        <h2 className="font-display font-bold text-xl">Red flags to avoid</h2>
                        <p className="text-sm text-muted-foreground mt-1">Curated list used during AI candidate analysis. Pick what matters for this role.</p>
                    </div>
                    <Button
                        type="button"
                        onClick={toggleAllFlags}
                        variant="outline"
                        className="rounded-none h-9 text-xs"
                        data-testid="red-flag-toggle-all"
                    >
                        {allChecked ? <Square className="w-4 h-4 mr-1.5" /> : <CheckSquare className="w-4 h-4 mr-1.5" />}
                        {allChecked ? "Uncheck all" : `Select all (${templates.length})`}
                    </Button>
                </div>
                <div className="grid md:grid-cols-2 gap-3">
                    {templates.map((t) => {
                        const checked = form.red_flags.includes(t);
                        return (
                            <label
                                key={t}
                                className={`flex items-start gap-3 border p-3 cursor-pointer transition-colors ${checked ? "border-ikb bg-blue-50/40" : "border-border bg-white hover:bg-secondary"}`}
                            >
                                <Checkbox checked={checked} onCheckedChange={() => toggleFlag(t)} className="mt-0.5 rounded-none" data-testid={`flag-${t.substring(0, 16)}`} />
                                <span className="text-sm leading-snug">{t}</span>
                            </label>
                        );
                    })}
                </div>
                <div className="pt-2">
                    <Label className="text-xs uppercase tracking-widest font-bold mb-2 block">Custom red flags</Label>
                    <TagInput
                        value={form.red_flags.filter((x) => !templates.includes(x))}
                        onChange={(custom) => set("red_flags", [...form.red_flags.filter((x) => templates.includes(x)), ...custom])}
                        placeholder="Add your own — press Enter"
                        testId="customflags"
                    />
                </div>
            </section>

            <div className="flex justify-end gap-3">
                <Button type="button" variant="outline" className="rounded-none h-11" onClick={onCancel} data-testid="job-cancel">Cancel</Button>
                <Button type="submit" disabled={busy} className="rounded-none bg-ikb hover:bg-[#001f7a] text-white h-11 px-8" data-testid="job-submit">
                    {busy ? "Saving..." : (mode === "edit" ? "Save changes" : "Publish job →")}
                </Button>
            </div>

            <Dialog open={confirmOpen} onOpenChange={(o) => { if (!o) { setConfirmOpen(false); setPendingPayload(null); } }}>
                <DialogContent className="rounded-none max-w-lg" data-testid="jobform-decline-confirm">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-amber-800">
                            <AlertTriangle className="w-5 h-5" />
                            {decliningInRequired.length} declining skill{decliningInRequired.length === 1 ? "" : "s"} in required
                        </DialogTitle>
                        <DialogDescription>
                            You&apos;re about to save this job with the following declining or obsolete skills as <b>must-haves</b>.
                            This may narrow your candidate pool to legacy talent. Continue anyway, or go back and swap them?
                        </DialogDescription>
                    </DialogHeader>
                    <ul className="text-xs space-y-1 max-h-52 overflow-y-auto border border-border p-2">
                        {decliningInRequired.map((t) => (
                            <li key={t.query} className="flex items-center justify-between">
                                <span><b>{t.query}</b> <span className="text-muted-foreground">· {t.trend_status}</span></span>
                                {t.trend_replaced_by && <span className="text-emerald-700">→ {t.trend_replaced_by}</span>}
                            </li>
                        ))}
                    </ul>
                    <DialogFooter>
                        <Button variant="outline" className="rounded-none" onClick={() => { setConfirmOpen(false); setPendingPayload(null); }} data-testid="jobform-confirm-back">
                            Back to edit
                        </Button>
                        <Button className="rounded-none bg-amber-600 hover:bg-amber-700" onClick={forceSubmit} data-testid="jobform-confirm-save">
                            Save anyway
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </form>
    );
}

function dedupeMerge(a = [], b = []) {
    const out = [...a];
    const seen = new Set(a.map((x) => x.toLowerCase()));
    for (const s of b) {
        if (!s) continue;
        const k = s.toLowerCase();
        if (!seen.has(k)) { out.push(s); seen.add(k); }
    }
    return out;
}
